/**
 * AuthoringRunRedirect — ADR-177 D3, `/fleet-editor/runs/:id` now lands on the workflow.
 *
 * The run tree moved onto the workflow editor page, where the context already is. This
 * route stays because a run URL is how one person hands another the evidence — "look at
 * this refusal" — and a surface whose only address is "the panel I happen to have open"
 * cannot be shared.
 *
 * IT HAS TO FETCH BEFORE IT CAN REDIRECT. The destination is
 * `/fleet-editor/agents/:agent/workflows/:workflow?run=:id`, and the id alone does not say
 * which slot it belongs to. So this reads the run, then sends the browser on.
 *
 * The three failures are kept apart, because they send a reader somewhere different: a run
 * that does not exist is a stale link, a read that failed is an outage, and an instance
 * with no store never recorded runs at all.
 */
import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'

import { api } from '../api/client'

export function AuthoringRunRedirect() {
  const { id = '' } = useParams()
  const [to, setTo] = useState<string | null>(null)
  const [state, setState] = useState<'loading' | 'missing' | 'error' | 'not_configured'>('loading')
  const [err, setErr] = useState('')

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const r = await api.authoringRunDetail(id)
        if (cancelled) return
        if (r.not_configured) { setState('not_configured'); return }
        if (!r.run) { setState('missing'); return }
        setTo(`/fleet-editor/agents/${encodeURIComponent(r.run.agent)}`
          + `/workflows/${encodeURIComponent(r.run.workflow)}?run=${encodeURIComponent(id)}`)
      } catch (e) {
        if (cancelled) return
        const msg = e instanceof Error ? e.message : 'the run could not be read'
        if (/404|not found|no such run/i.test(msg)) { setState('missing'); return }
        setErr(msg)
        setState('error')
      }
    })()
    return () => { cancelled = true }
  }, [id])

  // `replace`, so the back button returns to wherever the link was followed from rather
  // than to this resolver, which would bounce forward again.
  if (to) return <Navigate to={to} replace />

  return (
    <div className="mx-auto max-w-3xl space-y-3 p-4">
      {state === 'loading' && <div className="text-sm text-gray-600">Finding this run…</div>}

      {state === 'missing' && (
        <div className="rounded border border-gray-300 bg-gray-50 p-3 text-sm text-gray-700">
          There is no run with this id. It may predate runs being kept, or the link may be
          stale. <Link to="/fleet-editor/runs" className="text-indigo-700 hover:underline">
            See every run on this fleet
          </Link>.
        </div>
      )}

      {state === 'not_configured' && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          This Mission Control has no session store, so authoring runs are not recorded.
          The run may still have happened — it was not kept here.
        </div>
      )}

      {state === 'error' && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          <strong>Could not read this run.</strong> {err}
        </div>
      )}
    </div>
  )
}
